/**
 * Centralized API Configuration
 * 
 * This module provides standardized URL construction to prevent the double /api issue.
 * All services should use these utilities instead of constructing URLs manually.
 */

// Base configuration
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9003/api';

/**
 * Get the base API URL (always includes /api)
 * @returns {string} Base URL like "http://localhost:9003/api"
 */
export const getApiBaseUrl = () => {
  // Ensure the URL always ends with /api, regardless of environment variable format
  return BASE_URL.endsWith('/api') ? BASE_URL : `${BASE_URL}/api`;
};

/**
 * Get the server base URL (never includes /api)
 * @returns {string} Server URL like "http://localhost:9003"
 */
export const getServerBaseUrl = () => {
  const base = BASE_URL.endsWith('/api') ? BASE_URL.slice(0, -4) : BASE_URL;
  return base;
};

/**
 * Build an API endpoint URL
 * @param {string} endpoint - The endpoint path (should NOT start with /api)
 * @returns {string} Complete URL
 * 
 * @example
 * buildApiUrl('customers/123') // "http://localhost:9003/api/customers/123"
 * buildApiUrl('/customers/123') // "http://localhost:9003/api/customers/123"
 */
export const buildApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}/${cleanEndpoint}`;
};

/**
 * Build a non-API server URL (for OAuth, webhooks, etc.)
 * @param {string} path - The path (can include any prefix)
 * @returns {string} Complete URL
 * 
 * @example
 * buildServerUrl('qb/start') // "http://localhost:9003/qb/start"
 * buildServerUrl('/health') // "http://localhost:9003/health"
 */
export const buildServerUrl = (path) => {
  const baseUrl = getServerBaseUrl();
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${baseUrl}/${cleanPath}`;
};

/**
 * Legacy compatibility function for existing patterns
 * @deprecated Use buildApiUrl instead
 */
export const getApiUrl = buildApiUrl;

// Export configuration for debugging
export const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  serverUrl: getServerBaseUrl(),
  originalEnvVar: BASE_URL,
};

export default {
  getApiBaseUrl,
  getServerBaseUrl,
  buildApiUrl,
  buildServerUrl,
  API_CONFIG,
};